import { useState, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { ExpandMore, ExpandLess, CloudUpload } from '@material-ui/icons'

import { TOAST_TYPE } from '@base'
import { getInputType } from '@base/composite/Table/TableUtils/Columns'
import { Button, Input } from '@baseV2'
import { uploadBuyerInvoiceDocumentsApi, triggerPodMessage, downloadUrl } from '@data'
import { showToast, showLoader } from '@data/state/action'
import { updateDrawerMeta, toggleModal, toggleFileViewer } from '@data/state/action/root'
import { documentInvoiceType, strings, MODAL_TYPES } from '@constants'
import { paths } from '@routes'
import AddSiteManagerModal from './AddSiteManagerModal'
import { saveInvoice, buildBaseInvoicePayload } from './invoiceApi'

import {
	BasicDetailsCard,
	CardHeader,
	CardTitle,
	ChevronBtn,
	DetailGroup,
	DetailRow,
	DetailLabel,
	DetailValue,
	PodInnerCard,
	ViewDetailsLink,
	PodHeaderRight,
	FieldRight,
} from './style'
import { DocUploadButton } from '@pages/Profile/styles'

const POD = ({ invoiceData, order, supplierPoNumber, update, editable = false }) => {
	const dispatch = useDispatch()

	const [isOpen, setIsOpen] = useState(true)
	const [isSiteManagerOpen, setIsSiteManagerOpen] = useState(false)
	const [servicedWeight, setServicedWeight] = useState('')
	const [rejectedWeight, setRejectedWeight] = useState('')
	const [isSaving, setIsSaving] = useState(false)

	useEffect(() => {
		setServicedWeight(invoiceData?.total_serviced_weight ?? '')
		setRejectedWeight(invoiceData?.rejected_weight ?? '')
	}, [invoiceData?.total_serviced_weight, invoiceData?.rejected_weight])

	const hasSiteManager = !!invoiceData?.receiver_details?.user_id
	const hasWeightChanges =
		String(servicedWeight) !== String(invoiceData?.total_serviced_weight ?? '') ||
		String(rejectedWeight) !== String(invoiceData?.rejected_weight ?? '')

	const handleSave = useCallback(async () => {
		setIsSaving(true)
		const payload = {
			...invoiceData,
			...buildBaseInvoicePayload(invoiceData, order, supplierPoNumber),
			total_serviced_weight: servicedWeight !== '' ? Number(servicedWeight) : null,
			rejected_weight: rejectedWeight !== '' ? Number(rejectedWeight) : null,
		}
		await saveInvoice({
			payload,
			fallbackInvoice: {
				...invoiceData,
				total_serviced_weight: servicedWeight !== '' ? Number(servicedWeight) : null,
				rejected_weight: rejectedWeight !== '' ? Number(rejectedWeight) : null,
			},
			order,
			invoiceData,
			dispatch,
			supplierPoNumber,
			update,
		})
		setIsSaving(false)
	}, [servicedWeight, rejectedWeight, invoiceData, order, dispatch, supplierPoNumber, update])

	const onViewPod = () => {
		if (!invoiceData?.delivery_receipt) return
		dispatch(toggleFileViewer(true, { files: downloadUrl + invoiceData.delivery_receipt }))
	}

	const triggerHandler = () => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: strings('sure_want_to_trigger_pod_message'),
				confirmAction: (actionType) => {
					if (actionType) {
						dispatch(showLoader(true))
						triggerPodMessage(invoiceData?.receiver_details?.user_id, invoiceData?.original_invoice_number)
							.then((res) => {
								dispatch(showToast(true, res?.message, { type: TOAST_TYPE.SUCCESS }))
								update()
							})
							.catch((error) => {
								dispatch(showToast(true, error?.response?.data?.message, { type: TOAST_TYPE.ERROR }))
							})
							.finally(() => {
								dispatch(showLoader(false))
							})
					}
					dispatch(toggleModal(false))
				},
			})
		)
	}

	const uploadMultipleDoc = (files, type, key) => {
		const invoiceType = supplierPoNumber ? 'supplier' : 'buyer'
		if (files.length < 1) return
		dispatch(showLoader(true))
		const getPath = Array.from(files).map((f) => f)
		const updates = {}
		uploadBuyerInvoiceDocumentsApi(
			invoiceData.invoice_number,
			getPath.length > 0 ? getPath : [files],
			order.order_id,
			documentInvoiceType[type],
			invoiceType
		)
			.then((response) => {
				updates[key] = ['tc_received', 'dkt_documents'].includes(key)
					? [...(invoiceData[key] ?? []), ...response.file_paths]
					: response.file_paths[0]
				dispatch(
					updateDrawerMeta({
						invoiceData: {
							...invoiceData,
							...updates,
						},
					})
				)
				update()
			})
			.catch((err) => {
				dispatch(showToast(true, `${strings('document_upload_failed')} ${err}`, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const Text = () => {
		return (
			<>
				<CloudUpload fontSize='small' style={{ marginRight: 4 }} />
				{strings('upload')}
			</>
		)
	}

	return (
		<>
			<BasicDetailsCard>
				<CardHeader>
					<CardTitle>{strings('pod')}</CardTitle>
					<PodHeaderRight>
							{!supplierPoNumber && hasSiteManager && editable && (
							<Button variant='outline' color='primary' small onClick={triggerHandler}>
								{strings(['request', 'pod'])}
							</Button>
						)}
						{!supplierPoNumber && editable && (
							<Button variant='outline' color='primary' small onClick={() => setIsSiteManagerOpen(true)}>
								{[!hasSiteManager ? strings(['add', 'site', 'manager']) : strings(['edit', 'site', 'manager'])]}
							</Button>
						)}
						{editable && (
							<Button variant='solid' color='primary' small disabled={isSaving || !hasWeightChanges} onClick={handleSave}>
								{strings(['save', 'details'])}
							</Button>
						)}
						<ChevronBtn onClick={() => setIsOpen((v) => !v)}>{isOpen ? <ExpandLess /> : <ExpandMore />}</ChevronBtn>
					</PodHeaderRight>
				</CardHeader>
				{isOpen && (
					<PodInnerCard>
						<DetailGroup>
							{!supplierPoNumber && (
								<DetailRow>
									<DetailLabel>Site Manager Name</DetailLabel>
									<DetailValue>
										{invoiceData?.receiver_details?.full_name ?? '-'}
										{hasSiteManager && (
											<ViewDetailsLink onClick={() => setIsSiteManagerOpen(true)}>(View Details)</ViewDetailsLink>
										)}
									</DetailValue>
								</DetailRow>
							)}

							{!supplierPoNumber && (
								<DetailRow>
									<DetailLabel>{strings('pod_document')}</DetailLabel>
									<div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
										{invoiceData?.delivery_receipt && (
											<Button variant='outline' color='primary' small onClick={onViewPod}>
												{strings('view')}
											</Button>
										)}
										{editable && (
											<DocUploadButton
												label={Text()}
												xs
												uppercase
												onChange={(file) => uploadMultipleDoc(file, 'delivery_receipt', 'delivery_receipt')}
											/>
										)}
									</div>
								</DetailRow>
							)}

							<DetailRow>
								<DetailLabel>{strings(['total', 'serviced', 'quantity'])}</DetailLabel>
								<FieldRight>
									<Input
										type='number'
										small
										value={servicedWeight}
										onChange={(e) => setServicedWeight(e.target.value)}
										disabled={isSaving || !editable}
										rightIcon={getInputType(invoiceData?.order_items?.[0]?.input_type)}
									/>
								</FieldRight>
							</DetailRow>

							<DetailRow>
								<DetailLabel>{strings('rejected_weight')}</DetailLabel>
								<FieldRight>
									<Input
										type='number'
										small
										value={rejectedWeight}
										onChange={(e) => setRejectedWeight(e.target.value)}
										disabled={isSaving || !editable}
										rightIcon={getInputType(invoiceData?.order_items?.[0]?.input_type)}
									/>
								</FieldRight>
							</DetailRow>
						</DetailGroup>
					</PodInnerCard>
				)}
			</BasicDetailsCard>
			<AddSiteManagerModal
				isOpen={isSiteManagerOpen}
				onClose={() => setIsSiteManagerOpen(false)}
				order={order}
				invoiceData={invoiceData}
				organisationId={order?.buyer_list?.[0]?.buyer?.organisation_id}
				userId={invoiceData?.receiver_details?.user_id}
				editable={editable}
			/>
		</>
	)
}

export default POD
